/* eslint-disable complexity */
import { InfoButton } from 'components/buttons'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import classNames from 'classnames'
import { Bowed } from 'components/curves'
import useWindowSize from 'lib/hooks/useWindowSize'
import { useTracking } from 'lib/analytics'
import { HeroSection as HeroSectionType } from '../../LandingPage.types'
import css from './HeroSection.styles.scss'

interface HeroSectionProps {
  section: HeroSectionType
}

const HeroSection = ({ section }: HeroSectionProps) => {
  const [currentUser] = useCurrentUser()
  const tracker = useTracking()
  const {
    headerText,
    subHeaderText,
    mobileSubHeaderText,
    bulletList,
    callToAction,
    images: { desktop, mobile },
    orientation,
    desktopWidth,
    desktopContentIsRightAligned,
    isLongerImage,
    curvedBottom,
  } = section
  const hasContent =
    !!headerText || !!subHeaderText || !!bulletList || !!callToAction
  const windowWidth = useWindowSize(200).width
  const isSmallScreen = windowWidth && windowWidth < 576
  const isFullWidthSection = desktopWidth === 'full'
  const isStackedOrientation =
    orientation === 'stacked' && (isSmallScreen || !hasContent)
  const contentContainerClasses = classNames(
    css.HeroSection__contentContainer,
    {
      [css.HeroSection__contentContainerStacked]: isStackedOrientation,
      [css.HeroSection__contentContainerOverlay]: !isStackedOrientation,
      // Desktop content is always overlayed
      [css['HeroSection__contentContainerOverlay--rightAlignedDesktopContent']]:
        desktopContentIsRightAligned,
    }
  )

  const renderHeaderText = () => {
    if (!headerText) return null
    return <h1 className={css.HeroSection__headerText}>{headerText}</h1>
  }

  const renderSubHeaderText = () => {
    if (!subHeaderText) return null
    return (
      <h2 className={css.HeroSection__subHeaderText}>
        {isSmallScreen && mobileSubHeaderText
          ? mobileSubHeaderText
          : subHeaderText}
      </h2>
    )
  }

  const renderBulletList = () => {
    if (!bulletList || bulletList.length === 0) return null
    return (
      <ul className={css.HeroSection__bulletList}>
        {bulletList.map((bullet) => (
          <li
            className={css.HeroSection__bulletListItem}
            key={bullet.id}
            style={{ listStyleImage: `url(${bullet.icon})` }}
          >
            {bullet.text}
          </li>
        ))}
      </ul>
    )
  }

  const renderCallToAction = () => {
    if (!callToAction) return null
    if (callToAction.type === 'custom_component') {
      return callToAction.component
    }

    return (
      <InfoButton
        pill
        className={css.HeroSection__callToAction}
        href={callToAction.url}
        onClick={() => {
          if (typeof callToAction.onClick === 'function') {
            callToAction?.onClick(currentUser, tracker)
          }
        }}
      >
        {callToAction.text}
      </InfoButton>
    )
  }

  return (
    <div
      className={classNames(css.HeroSection__container, {
        [css['HeroSection__container--fullWidthSection']]: isFullWidthSection,
        [css['HeroSection__container--stackedOrientation']]:
          isStackedOrientation,
      })}
    >
      <div
        className={classNames(css.HeroSection__heroImageContainer, {
          [css['HeroSection__heroImageContainer--stackedOrientation']]:
            isStackedOrientation,
          [css['HeroSection__heroImageContainer--longerImage']]: isLongerImage,
          [css['HeroSection__heroImageContainer--curvedBottom']]: curvedBottom,
        })}
      >
        <img
          alt="Hero"
          className={classNames(css.HeroSection__heroImage, {
            [css['HeroSection__heroImage--stackedOrientation']]:
              isStackedOrientation,
            [css['HeroSection__heroImage--longerImage']]: isLongerImage,
          })}
          src={isSmallScreen ? mobile : desktop}
        />
      </div>
      {hasContent && (
        <div className={contentContainerClasses}>
          <div className={css.HeroSection__content}>
            {renderHeaderText()}
            {renderSubHeaderText()}
            {renderBulletList()}
            {renderCallToAction()}
            {isSmallScreen && orientation === 'stacked' && (
              <Bowed position="bottom" />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default HeroSection
